import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const EMPTY_ARRAY = [];

export default function GlobalPopup({ registrations = EMPTY_ARRAY }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    const fetchPopup = async () => {
      try {
        const { data } = await api.get("/meta/popup");
        if (data && data.enabled && (data.title || data.text || data.image_url)) {
          // Check target audience
          let shouldShow = false;
          const target = data.target || "everyone";
          const isIndia = user?.nationality === "India";
          let hasPaid = registrations.some(r => r.payment_status === "paid");

          if ((target === "paid" || target === "unpaid") && registrations.length === 0) {
            try {
              const dashRes = await api.get("/dashboard");
              hasPaid = dashRes.data?.registrations?.some(r => r.payment_status === "paid");
            } catch(e) { }
          }

          if (target === "everyone") shouldShow = true;
          else if (target === "indians" && isIndia) shouldShow = true;
          else if (target === "internationals" && !isIndia) shouldShow = true;
          else if (target === "paid" && hasPaid) shouldShow = true;
          else if (target === "unpaid" && !hasPaid) shouldShow = true;
          else if (target === "specific" && data.target_email && user?.email === data.target_email) shouldShow = true;

          // Always show to admins so they can preview their changes
          if (user?.role === "admin") shouldShow = true;

          if (shouldShow) {
            // Check if seen (use a hash of the content so if admin changes it, it shows again)
            // A simple hash string can just be the title+text length or image_url. 
            // Better: just use a static key but if we want to re-show we need an ID. 
            // For now, we'll store a json string of the config to know if it changed.
            // Using updated_at ensures that clicking Save forces a re-show even if content didn't change!
            const popupHash = btoa(encodeURIComponent(`${data.title}|${data.text}|${data.updated_at || ''}`));
            const storageKey = `hasSeenGlobalPopup_${user?.user_id || 'anon'}`;
            const seenHash = localStorage.getItem(storageKey);

            if (seenHash !== popupHash) {
              setConfig(data);
              setTimeout(() => setOpen(true), 600);
              // Mark as seen immediately so it doesn't trigger again on re-renders
              localStorage.setItem(storageKey, popupHash);
            }
          }
        }
      } catch (e) {
        // silently fail
      }
    };

    if (user) {
      fetchPopup();
    }
  }, [user, registrations]);

  const handleClose = () => {
    setOpen(false);
    if (config) {
      const popupHash = btoa(encodeURIComponent(`${config.title}|${config.text}|${config.updated_at || ''}`));
      const storageKey = `hasSeenGlobalPopup_${user?.user_id || 'anon'}`;
      localStorage.setItem(storageKey, popupHash);
    }
  };

  if (!config) return null;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) handleClose(); }}>
      <DialogContent className="sm:max-w-md bg-[#142040] border border-white/10 text-white">
        {config.title && (
          <DialogHeader>
            <DialogTitle className="text-xl font-display font-bold text-[#00F0FF]">{config.title}</DialogTitle>
          </DialogHeader>
        )}
        
        {config.image_url && (
          <div className="mt-2 rounded-lg overflow-hidden border border-white/5 bg-black/20">
            <img src={config.image_url} alt="Popup" className="w-full h-auto object-cover max-h-64" />
          </div>
        )}

        {config.text && (
          <DialogDescription className="text-white mt-4 whitespace-pre-wrap text-sm leading-relaxed">
            {config.text}
          </DialogDescription>
        )}

        <div className="mt-6 flex justify-end">
          <button onClick={handleClose} className="btn-primary">
            Dismiss
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
